import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Search, FileText, Plus, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ApplicationChat from '../components/ApplicationChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = () => axios.create({
  baseURL: API,
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const STATUS_STYLES = {
  approved: 'bg-emerald-100 text-emerald-700',
  offer_accepted: 'bg-emerald-100 text-emerald-700',
  enrolled: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  refused: 'bg-rose-100 text-rose-700',
  in_review: 'bg-amber-100 text-amber-700',
  submitted: 'bg-blue-100 text-blue-700',
  pending: 'bg-gray-100 text-gray-700',
};

const AgentMyApplicationsPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // ----- New Application dialog state ---------------------------------
  const [createOpen, setCreateOpen] = useState(params.get('new') === '1');

  // ----- Chat dialog state ---------------------------------
  const [chatApp, setChatApp] = useState(null);
  const [unreadByApp, setUnreadByApp] = useState({});

  const loadUnreadCounts = useCallback(async () => {
    try {
      const r = await api().get('/chat/unread-count');
      setUnreadByApp(r.data?.by_application || {});
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadUnreadCounts();
    const t = setInterval(loadUnreadCounts, 30000);
    return () => clearInterval(t);
  }, [loadUnreadCounts]);

  const openChat = async (row) => {
    setChatApp(row);
    // Eagerly mark this app's messages as read so the badge clears
    try {
      await api().post(`/applications/${row.id}/chat/mark-read`);
      setUnreadByApp((prev) => ({ ...prev, [row.id]: 0 }));
    } catch (_e) { /* ignore */ }
  };
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    student_name: '', student_email: '', student_phone: '',
    first_name: '', last_name: '', date_of_birth: '', gender: '',
    nationality: '', passport_number: '',
  });
  const resetForm = () => setForm({
    student_name: '', student_email: '', student_phone: '',
    first_name: '', last_name: '', date_of_birth: '', gender: '',
    nationality: '', passport_number: '',
  });
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.student_name || !form.student_email) {
      toast.error('Full name and email are required');
      return;
    }
    setCreating(true);
    try {
      const submit = {
        student_name: form.student_name,
        student_email: form.student_email,
        student_phone: form.student_phone,
        desk_data: { counselor: {
          first_name: form.first_name, last_name: form.last_name,
          dob: form.date_of_birth, gender: form.gender,
          nationality: form.nationality, passport: form.passport_number,
        } },
      };
      const r = await api().post('/applications', submit);
      toast.success('Application created');
      setCreateOpen(false);
      resetForm();
      navigate(`/agent/applications/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create application');
    }
    setCreating(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const r = await api().get('/agent-portal/me/applications', { params });
      setRows(r.data?.applications || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load applications');
    }
    setLoading(false);
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [statusFilter, load]);

  const pretty = (s) => (s || 'pending').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto" data-testid="agent-my-applications">
      <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-[#F5A623]" />
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A2A4F]">My Applications</h1>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#F5A623] hover:bg-[#E08F0C] text-white" data-testid="agent-new-app-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Application
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Application</DialogTitle>
              <DialogDescription>Refer a student. You can add program / university / documents after creation.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2" data-testid="agent-new-app-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="agent-app-firstname" />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="agent-app-lastname" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Full Name <span className="text-rose-500">*</span></Label>
                <Input value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} required data-testid="agent-app-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email <span className="text-rose-500">*</span></Label>
                  <Input type="email" value={form.student_email} onChange={(e) => setForm({ ...form, student_email: e.target.value })} required data-testid="agent-app-email" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={form.student_phone} onChange={(e) => setForm({ ...form, student_phone: e.target.value })} data-testid="agent-app-phone" placeholder="+44 ..." />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="agent-app-dob" />
                </div>
                <div className="space-y-2">
                  <Label>Nationality</Label>
                  <Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} data-testid="agent-app-nationality" />
                </div>
                <div className="space-y-2">
                  <Label>Passport No.</Label>
                  <Input value={form.passport_number} onChange={(e) => setForm({ ...form, passport_number: e.target.value })} data-testid="agent-app-passport" />
                </div>
              </div>
              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={creating} className="bg-[#1A2A4F] text-white" data-testid="agent-app-submit">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
                  Create Application
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <p className="text-sm text-gray-500 mb-6">Every application you have referred or created.</p>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="Search student, program, university…"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
            data-testid="apps-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
          data-testid="apps-status-filter"
        >
          <option value="">All statuses</option>
          <option value="submitted">Submitted</option>
          <option value="in_review">In review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="enrolled">Enrolled</option>
        </select>
        <button
          onClick={load}
          className="px-4 py-2 bg-[#1A2A4F] text-white text-sm font-semibold rounded-md hover:brightness-110"
          data-testid="apps-refresh-btn"
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-gray-400" data-testid="apps-empty-state">
            No applications match your filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="apps-table">
              <thead className="bg-gray-50 text-left">
                <tr className="text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 font-bold">#</th>
                  <th className="px-4 py-3 font-bold">Student</th>
                  <th className="px-4 py-3 font-bold">Program</th>
                  <th className="px-4 py-3 font-bold">University</th>
                  <th className="px-4 py-3 font-bold">Intake</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 font-bold">Created</th>
                  <th className="px-4 py-3 font-bold text-center">Chat</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const name = r.student_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || '—';
                  const status = r.status || 'pending';
                  const cls = STATUS_STYLES[status] || STATUS_STYLES.pending;
                  return (
                    <tr
                      key={r.id}
                      className="border-t border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                      onClick={() => navigate(`/agent/applications/${r.id}`)}
                      data-testid={`apps-row-${r.id}`}
                    >
                      <td className="px-4 py-3 font-mono text-gray-400">{r.application_number || idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#1A2A4F]">{name}</div>
                        <div className="text-xs text-gray-500">{r.student_email || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.program_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{r.university_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{r.intake || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${cls}`}>
                          {pretty(status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td
                        className="px-4 py-3 text-center"
                        onClick={(e) => { e.stopPropagation(); openChat(r); }}
                      >
                        <button
                          type="button"
                          className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F5A623]/10 hover:bg-[#F5A623]/20 text-[#F5A623] transition-colors"
                          data-testid={`chat-btn-${r.id}`}
                          title="Chat with counsellor"
                        >
                          <MessageCircle className="w-4 h-4" />
                          {unreadByApp[r.id] > 0 && (
                            <span
                              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
                              data-testid={`chat-badge-${r.id}`}
                            >
                              {unreadByApp[r.id] > 99 ? '99+' : unreadByApp[r.id]}
                            </span>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Chat dialog (per application) */}
      <ApplicationChat
        open={!!chatApp}
        onOpenChange={(o) => { if (!o) { setChatApp(null); loadUnreadCounts(); } }}
        application={chatApp}
      />
    </div>
  );
};

export default AgentMyApplicationsPage;
